<script lang="ts">
	import type { Application } from "src/types/types.ts";
	import { actions } from "astro:actions";
	import { onMount } from "svelte";
	import { getUser } from "@stores/user";
	import JobOfferWiggles from "@components/sections/JobOfferWiggles.svelte";
	import { fetchDistinctId } from "@lib/posthogEvents";
	import { ApplicationLocation, ApplicationMethod } from "@lib/analyticsTypes";
	import { algoliaFireUserAppliedEvent } from "@stores/algolia";
	import { clarityFireApplicationEvent } from "@lib/clarityEvents";
	import { toast } from "svelte-sonner";
	import Button from "@components/inputs/Button.svelte";
	import RegisterWithPasswordOnlyForm from "@components/auth/RegisterWithPasswordOnlyForm.svelte";

	export let application_location: ApplicationLocation;
	export let company_uuid: Application["company_uuid"];
	export let job_uuid: Application["job_uuid"];
	export let job_title: Application["job_title"];
	export let subtitle: string;
	export let logoUrl: string = "";
	export let border: string = "border-[8px] border-company-secondary";
	export let flexDirection: string = "flex-row";

	let success = false;
	let error = false;

	onMount(async () => {
		const user = await getUser();

		if (user) {
			if (user.user_metadata?.firstName) {
				name = user.user_metadata.firstName;
			}
			if (user.user_metadata?.lastName) {
				name += " " + user.user_metadata.lastName;
			}

			if (user?.email) {
				email = user.email;
			}

			if (user?.user_metadata?.phone) {
				phoneNumber = user.user_metadata.phone;
			}
		}
	});

	let name = "";
	let email = "";
	let phoneNumber = "";
	let loading = false;

	async function handleSubmit(e: Event) {
		if (loading || error || success) {
			return;
		}

		loading = true;
		e.preventDefault();

		// The distinct_id of the current PostHog user is fetched
		const distinct_id = fetchDistinctId();

		// If a user has a session, the application_method is direct_apply.
		// Otherwise, it's a regular form_submission.
		const user = await getUser();

		const application_method = user
			? ApplicationMethod.DirectApply
			: ApplicationMethod.FormSubmission;

		try {
			const { error } = await actions.application.applyForJob({
				name,
				email,
				phone: phoneNumber,
				job_title,
				company_uuid,
				job_uuid,
				distinct_id,
				application_location,
				application_method
			});

			if (!error) {
				success = true;
				clarityFireApplicationEvent(
					job_uuid,
					company_uuid,
					ApplicationMethod.FormSubmission,
					application_location
				);

				// Optional: Fire Algolia event
				algoliaFireUserAppliedEvent({
					objectID: job_uuid
				});
			} else {
				throw new Error(error?.message || "Er is iets fout gegaan.");
			}
		} catch (err) {
			toast.error("Er is iets fout gegaan.");
			error = true;
			// For 5 seconds, show the error message.
			setTimeout(() => {
				error = false;
			}, 5000);
		} finally {
			loading = false;
		}
	}
</script>

<form
	id="applicationForm"
	on:submit={handleSubmit}
	class="relative flex h-full min-h-96 flex-col items-center justify-start overflow-hidden rounded-3xl bg-white p-2 text-center transition-colors lg:w-96 lg:justify-center {border}"
>
	<!-- Rest of the form remains the same as in the previous version -->
	<div class=" flex flex-wrap items-center justify-center {flexDirection}">
		<div
			class="bg-company-primary-light mt-4 flex items-center justify-center rounded-full p-10 lg:p-14"
		>
			<img
				class={"absolute mx-auto max-h-20 w-16 object-contain lg:w-24"}
				alt="logo"
				src={logoUrl}
			/>
		</div>

		{#if $$slots.title}
			<slot name="title" />
		{:else}
			<h3 class={`!text-shadow mb-4 p-4 text-5xl font-[900]   text-black`}>
				{subtitle}
			</h3>
		{/if}
	</div>
	<div class="relative">
		<div
			class="relative flex flex-shrink-0 flex-col gap-6 p-4 transition-opacity {success
				? 'opacity-0'
				: 'opacity-100'}"
		>
			<label class="text-start">
				<span class="shadow-company-primary font-[900] text-black"> Naam </span>
				<input
					bind:value={name}
					placeholder="Naam"
					class="focus:border-company-secondary peer w-full rounded-lg bg-gray-200 px-2 py-1 outline-none transition-colors aria-[invalid=true]:border-red-500"
					name="name"
					type="text"
					required
				/>
			</label>
			<label class="text-start">
				<span class="shadow-company-primary font-[900] text-black">
					Email
				</span>

				<input
					bind:value={email}
					placeholder="Email"
					class="focus:border-company-secondary peer w-full rounded-lg bg-gray-200 px-2 py-1 outline-none transition-colors aria-[invalid=true]:border-red-500"
					name="email"
					type="email"
					required
				/>
			</label>
			<label class="text-start">
				<span class="shadow-company-primary font-[900] text-black">
					Telefoon nummer (optioneel)
				</span>

				<input
					bind:value={phoneNumber}
					placeholder="Telefoon nummer (optioneel)"
					class="focus:border-company-secondary peer w-full rounded-lg bg-gray-200 px-2 py-1 outline-none transition-colors aria-[invalid=true]:border-red-500"
					name="phone"
					type="tel"
				/>
			</label>
		</div>
		{#if !success}
			<Button
				type="submit"
				css=" mb-4 mt-4 mx-auto"
				width="w-auto"
				shadow
				color="text-emerald-500"
				textColor="text-white"
				{loading}
			>
				Verzenden
			</Button>
		{/if}
		<div
			class={` ${success ? "absolute top-8 flex w-full flex-col gap-2 text-center text-green-500" : "text-red-500"}`}
		>
			{#if success}
				{#await getUser() then user}
					{#if !user}
						<RegisterWithPasswordOnlyForm {email} {name} {phoneNumber} />
					{:else}
						<div class="text-center">
							Bedankt voor je sollicitatie! Je ontvangt binnenkort een e-mail
							van ons.
						</div>
						<Button
							href="/"
							type="submit"
							css="font-[900] mb-4 mt-4 mx-auto"
							width="w-auto"
							shadow
							color="text-emerald-500"
							textColor="text-white"
						>
							Naar overzicht
						</Button>
					{/if}
				{/await}
			{/if}
		</div>
	</div>
	<JobOfferWiggles
		fill="fill-black"
		class="absolute left-0 top-0 z-10  flex h-full w-full scale-150 justify-center fill-black opacity-20"
	/>
</form>
